import type { Shot } from './types';

export const SHOTS_MAX = 6;
export const SHOT_SIDE = 2200;
/** PNG до этого веса едет как есть: текст на скрине остаётся резким. */
const PNG_KEEP = 1_200_000;
/** Сервер не примет тяжелее 3 МБ; держим запас на base64. */
const SHOT_BYTES = 2_800_000;

export function fitSize(w: number, h: number, max = SHOT_SIDE): { w: number; h: number } {
  if (w <= 0 || h <= 0) return { w: 0, h: 0 };
  const scale = Math.min(1, max / Math.max(w, h));
  return { w: Math.round(w * scale), h: Math.round(h * scale) };
}

/** Картинки из буфера, перетаскивания или выбора файла. Текст и прочее мимо. */
export function imagesOf(items: DataTransferItemList | FileList | null | undefined): File[] {
  if (!items) return [];
  const found: File[] = [];
  for (let i = 0; i < items.length; i += 1) {
    const item = items[i];
    if (!item) continue;
    if (item instanceof File) {
      if (item.type.startsWith('image/')) found.push(item);
      continue;
    }
    const entry = item as DataTransferItem;
    if (entry.kind === 'file' && entry.type.startsWith('image/')) {
      const file = entry.getAsFile();
      if (file) found.push(file);
    }
  }
  return found;
}

export function dataBytes(dataUrl: string): number {
  const payload = dataUrl.slice(dataUrl.indexOf(',') + 1);
  return Math.floor((payload.length * 3) / 4);
}

function readUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('скрин не прочитался'));
    reader.readAsDataURL(file);
  });
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('это не картинка'));
    img.src = url;
  });
}

let seq = 0;

/** Файл → ужатый скрин. Большой PNG и фото уходят JPEG, длинная сторона до 2200. */
export async function shotOf(file: File): Promise<Shot> {
  const original = await readUrl(file);
  const img = await loadImage(original);
  const size = fitSize(img.naturalWidth, img.naturalHeight);
  seq += 1;
  const key = `${Date.now()}-${seq}`;
  const keep = file.type === 'image/png' && file.size <= PNG_KEEP && size.w === img.naturalWidth;
  if (keep) return { key, data: original, w: size.w, h: size.h, size: file.size };
  const canvas = document.createElement('canvas');
  canvas.width = size.w;
  canvas.height = size.h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('браузер не дал ужать скрин');
  ctx.fillStyle = '#fff';
  ctx.fillRect(0, 0, size.w, size.h);
  ctx.drawImage(img, 0, 0, size.w, size.h);
  let quality = 0.9;
  let data = canvas.toDataURL('image/jpeg', quality);
  while (dataBytes(data) > SHOT_BYTES && quality > 0.5) {
    quality -= 0.15;
    data = canvas.toDataURL('image/jpeg', quality);
  }
  if (dataBytes(data) > SHOT_BYTES) throw new Error('скрин слишком тяжёлый');
  return { key, data, w: size.w, h: size.h, size: dataBytes(data) };
}
